import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Button,
  IconButton,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Chip,
  useTheme,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import { LANGUAGES } from '../languages';

type Props = {
  open: boolean;
  selectedLanguages: [keyof typeof LANGUAGES, string][];
  onAdd: (code: keyof typeof LANGUAGES) => void;
  onChange: (oldCode: keyof typeof LANGUAGES, code: keyof typeof LANGUAGES) => void;
  onDelete: (code: keyof typeof LANGUAGES) => void;
  onUpdate: (code: string, displayName: string) => void;
  onClose: () => void;
};

export default function SelectLanguagesModal({
  open,
  selectedLanguages,
  onAdd,
  onChange,
  onDelete,
  onUpdate,
  onClose,
}: Props) {
  const { t } = useTranslation();
  const theme = useTheme();

  // Add an empty row at the bottom for new selection
  const languageRows: [keyof typeof LANGUAGES | '', string][] = [...selectedLanguages, ['', '']];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{t('manage_languages')}</DialogTitle>

      <DialogContent>
        {/* Table header */}
        <Box
          display="flex"
          gap={2}
          mb={1}
        >
          <Box
            flex={1}
            fontWeight="bold"
          >
            {t('language')}
          </Box>
          <Box
            flex={1}
            fontWeight="bold"
          >
            {t('display_as')}
          </Box>
        </Box>

        {/* Rows */}
        {languageRows.map((code, index) => {
          // @ts-expect-error
          const lang = LANGUAGES[code] || { name: '', flag: '' };

          return (
            <Box
              key={index}
              display="flex"
              alignItems="center"
              gap={2}
              mb={1}
            >
              {/* Delete */}
              <IconButton
                size="small"
                onClick={() => onDelete(code[0] as keyof typeof LANGUAGES)}
                disabled={!code}
              >
                <DeleteIcon color={code ? 'error' : 'disabled'} />
              </IconButton>

              {/* Language select */}
              <FormControl
                fullWidth
                sx={{ minWidth: 140 }}
              >
                <InputLabel>{t('language')}</InputLabel>
                <Select
                  value={code[0]}
                  label={t('language')}
                  autoFocus={!code && index === languageRows.length - 1}
                  onChange={(e) => {
                    const newCode = e.target.value as keyof typeof LANGUAGES;
                    if (newCode && !selectedLanguages.some((l) => l[0] === newCode)) {
                      if (code[0]) {
                        onChange(code[0] as keyof typeof LANGUAGES, newCode);
                      } else {
                        onAdd(newCode);
                      }
                    }
                  }}
                >
                  {Object.entries(LANGUAGES).map(([langCode, lang]) => (
                    <MenuItem
                      key={langCode}
                      value={langCode}
                      disabled={selectedLanguages.some((l) => l[0] === langCode)}
                    >
                      <Box
                        component="img"
                        src={lang.flag}
                        alt={lang.name}
                        sx={{ mr: '.2rem' }}
                      />
                      {lang.name}{' '}
                      {lang.beta && (
                        <Typography
                          variant={'body2'}
                          fontSize={'0.5rem'}
                          sx={{ color: theme.palette.primary.main, ml: 'auto' }}
                        >
                          BETA
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Display name field */}
              <TextField
                fullWidth
                value={code[1]}
                placeholder={t('enter_display_name')}
                disabled={!code}
                onChange={(e) => onUpdate(code[0], e.target.value)}
              />
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>{t('cancel')}</Button>
        <Button
          variant="contained"
          color="success"
          onClick={onClose}
        >
          {t('ok')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
